import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { clientsApi } from '../lib/api';
import api from '../lib/api';
import { formatDate, getStatusColor, capitalizeFirst, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Filter, MoreHorizontal, Eye, Edit, UserCheck, FileText,
  Phone, Mail, MapPin, Briefcase, CreditCard, ChevronLeft, ChevronRight, ScanLine, Upload, AlertCircle,
  CheckCircle2, Clock, XCircle, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ExportButton from '../components/ExportButton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { ScreeningCard } from './ComplianceHubPage';
import { useAuth } from '../contexts/AuthContext';

const ClientComplianceTab = ({ client, onReload }) => {
  const { user } = useAuth();
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescreening, setRescreening] = useState(false);

  const canDecide = ['admin', 'manager', 'compliance_officer'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sanctions/clients/${client.id}/screenings`);
      setScreenings(res.data.screenings || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { if (client?.id) load(); }, [client?.id]);

  const handleRescreen = async () => {
    setRescreening(true);
    try {
      await api.post(`/sanctions/clients/${client.id}/rescreen`);
      toast.success('Re-screening complete');
      load();
      if (onReload) onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-screen failed'); }
    setRescreening(false);
  };

  const latest = screenings[0];

  return (
    <div className="space-y-4" data-testid="client-compliance-content">
      <div className="bg-white border border-gray-200 p-4 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-[#1f1f1f]">Sanctions / PEP Compliance</h4>
          <p className="text-xs text-gray-500 mt-1">FIC Act Customer Due Diligence — last screening: {latest ? latest.screened_at?.slice(0, 16).replace('T', ' ') : 'Never'}</p>
          {client.status === 'sanctions_blocked' && <p className="text-xs text-red-700 mt-1 font-semibold">⚠ Client account is BLOCKED pending compliance review.</p>}
          {client.status === 'sanctions_escalated' && <p className="text-xs text-amber-700 mt-1 font-semibold">⚠ Client account is ESCALATED.</p>}
        </div>
        <Button onClick={handleRescreen} disabled={rescreening} variant="outline" data-testid="rescreen-btn"><ShieldCheck className="w-4 h-4 mr-2" />{rescreening ? 'Re-screening...' : 'Re-screen now'}</Button>
      </div>

      {loading && <p className="text-gray-400 text-center py-8">Loading screenings...</p>}
      {!loading && screenings.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 p-6 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-800">No screenings on file</p>
          <p className="text-xs text-emerald-700 mt-1">Click "Re-screen now" to run a fresh check.</p>
        </div>
      )}
      {screenings.map(s => <ScreeningCard key={s.id} screening={s} onReload={() => { load(); if (onReload) onReload(); }} canDecide={canDecide} />)}
    </div>
  );
};

// =============================================================================
// KYC Trust Score Panel — 2-column verification summary on the client profile.
// Aggregates: bureau prefill source, ID-name match, sanctions/PEP status,
// DHA photo (when DHA Direct returns one), and a Re-verify button.
// =============================================================================
const KYCTrustPanel = ({ client, onReload }) => {
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reverifying, setReverifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/integrations/kyc/trust/${client.id}`);
      setTrust(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (client?.id) load(); }, [client?.id]);

  const handleReverify = async () => {
    setReverifying(true);
    try {
      const { data } = await api.post(`/integrations/kyc/reverify/${client.id}`);
      setTrust(data);
      toast.success('KYC re-verification complete');
      if (onReload) onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-verification failed');
    }
    setReverifying(false);
  };

  const idMatchPill = (status) => {
    const map = {
      TRUE:        { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: <CheckCircle2 className="w-3 h-3" />, label: 'TRUE'         },
      FALSE:       { cls: 'bg-red-100 text-red-800 border-red-300',             icon: <XCircle className="w-3 h-3" />,        label: 'FALSE'        },
      NOT_CHECKED: { cls: 'bg-gray-100 text-gray-700 border-gray-300',          icon: <Clock className="w-3 h-3" />,          label: 'NOT_CHECKED'  },
    };
    const m = map[status] || map.NOT_CHECKED;
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 border ${m.cls}`} data-testid={`kyc-id-match-${status}`}>
        {m.icon}{m.label}
      </span>
    );
  };

  const sanctionsPill = (s) => {
    const status = s?.status || 'not_checked';
    const map = {
      clear:       { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: <CheckCircle2 className="w-3 h-3" />, label: 'CLEAR'       },
      review:      { cls: 'bg-amber-100 text-amber-900 border-amber-300',       icon: <AlertTriangle className="w-3 h-3" />, label: 'REVIEW'      },
      match:       { cls: 'bg-red-100 text-red-800 border-red-300',             icon: <XCircle className="w-3 h-3" />,        label: 'MATCH'       },
      not_checked: { cls: 'bg-gray-100 text-gray-700 border-gray-300',          icon: <Clock className="w-3 h-3" />,          label: 'NOT_CHECKED' },
    };
    const m = map[status] || map.not_checked;
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 border ${m.cls}`} data-testid={`kyc-sanctions-${status}`}>
        {m.icon}{m.label}
        {s?.hit_count > 0 && <span className="ml-1">({s.hit_count} hit{s.hit_count > 1 ? 's' : ''})</span>}
      </span>
    );
  };

  const fmtTime = (iso) => iso ? iso.slice(0, 16).replace('T', ' ') : '—';

  return (
    <div className="bg-white border border-gray-200" data-testid="kyc-trust-panel">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div>
          <h4 className="text-sm font-semibold text-[#1f1f1f] flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#C5A059]" />KYC Verification
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Bureau prefill, ID-name match, sanctions/PEP, and Home Affairs photo — last verified {fmtTime(trust?.last_verified_at)}
          </p>
        </div>
        <Button onClick={handleReverify} disabled={reverifying || loading}
                className="bg-[#1f1f1f] hover:bg-[#333333] h-9" data-testid="kyc-reverify-btn">
          <ShieldCheck className="w-4 h-4 mr-2" />{reverifying ? 'Re-verifying…' : 'Re-verify'}
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-8 text-sm">Loading KYC trust state…</p>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 p-4">
          {/* Left col */}
          <div className="space-y-4">
            <div data-testid="kyc-source-row">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Source (which bureau prefilled)</p>
              {trust?.source ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 bg-[#FFFDF5] border border-[#C5A059] text-[#8B7339]">
                  <ScanLine className="w-3 h-3" />{trust.source}
                </span>
              ) : (
                <span className="text-xs text-gray-400 italic">No bureau lookup performed yet</span>
              )}
            </div>

            <div data-testid="kyc-idmatch-row">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Bureau ID-name match</p>
              <div className="flex items-center gap-2">{idMatchPill(trust?.id_match || 'NOT_CHECKED')}</div>
              {trust?.id_match_detail && (
                <p className="text-[11px] text-gray-500 mt-1 leading-snug">{trust.id_match_detail}</p>
              )}
            </div>

            <div data-testid="kyc-sanctions-row">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Sanctions / PEP screening</p>
              <div className="flex items-center gap-2">{sanctionsPill(trust?.sanctions)}</div>
              {trust?.sanctions?.screened_at && (
                <p className="text-[11px] text-gray-500 mt-1">Screened {fmtTime(trust.sanctions.screened_at)}</p>
              )}
              {trust?.sanctions?.decision && (
                <p className="text-[11px] text-gray-500 mt-1">Compliance decision: <b>{trust.sanctions.decision}</b></p>
              )}
            </div>
          </div>

          {/* Right col — DHA photo */}
          <div data-testid="kyc-photo-row">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Home Affairs photo (DHA Direct)</p>
            {trust?.dha_photo ? (
              <div className="border border-gray-200 inline-block bg-gray-50">
                <img src={trust.dha_photo} alt="DHA ID photo"
                     className="w-32 h-40 object-cover" data-testid="kyc-dha-photo-img" />
                <p className="text-[10px] text-gray-500 px-2 py-1 border-t border-gray-200 text-center">Returned by DHA Direct</p>
              </div>
            ) : (
              <div className="w-32 h-40 border border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-center px-2">
                <AlertCircle className="w-5 h-5 text-gray-400 mb-1" />
                <p className="text-[10px] text-gray-500 leading-tight">
                  No photo on file. Configure a DHA Direct provider in Admin → External Services and click Re-verify.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const officeId = searchParams.get('office_id');
  const officeName = searchParams.get('office_name');
  const actionParam = searchParams.get('action');

  useEffect(() => {
    if (actionParam === 'new') setShowNewModal(true);
  }, [actionParam]);

  useEffect(() => {
    loadClients();
  }, [page, statusFilter, officeId]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const params = { skip: page * 20, limit: 20 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      if (officeId) params.office_id = officeId;
      const response = await clientsApi.list(params);
      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearOfficeFilter = () => {
    navigate('/dashboard/clients');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadClients();
  };

  const viewClient = async (clientId) => {
    try {
      const response = await clientsApi.get(clientId);
      setSelectedClient(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load client:', error);
    }
  };

  const activateClient = async (clientId) => {
    try {
      await clientsApi.activate(clientId);
      loadClients();
    } catch (error) {
      console.error('Failed to activate client:', error);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="clients-page">
      {/* Office Filter Banner */}
      {officeId && (
        <div className="bg-[#1f1f1f]/5 border border-[#1f1f1f]/10 px-4 py-2 flex items-center justify-between" data-testid="office-filter-banner">
          <span className="text-sm text-[#1f1f1f]">Filtered by office: <strong>{decodeURIComponent(officeName || 'Selected Office')}</strong></span>
          <div className="flex gap-2">
            <button onClick={() => navigate(`/dashboard/loans?office_id=${officeId}&office_name=${officeName}`)} className="text-xs text-blue-600 hover:underline">View Loans</button>
            <button onClick={() => navigate(`/dashboard/savings?office_id=${officeId}&office_name=${officeName}`)} className="text-xs text-blue-600 hover:underline">View Savings</button>
            <button onClick={clearOfficeFilter} className="text-xs text-red-500 hover:underline">Clear Filter</button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
            CLIENT MANAGEMENT
          </p>
          <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
            Clients
          </h1>
        </div>
        <div className="flex gap-2">
          <ExportButton entity="clients" label="Clients" />
          <Button
            onClick={() => setShowNewModal(true)}
            className="bg-[#1f1f1f] hover:bg-[#333333]"
            data-testid="new-client-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Client
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, ID number, or phone..."
              className="pl-10"
              data-testid="client-search-input"
            />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="blacklisted">Blacklisted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="clients-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  ID Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Credit Score
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No clients found
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => viewClient(client.id)} data-testid={`client-row-${client.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1f1f1f] text-white flex items-center justify-center font-medium">
                          {client.first_name?.[0]}{client.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-sm text-gray-500">{client.client_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {client.id_number}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{client.phone}</p>
                        <p className="text-gray-500">{client.email || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {client.credit_score ? (
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${
                            client.credit_score >= 700 ? 'text-emerald-600' :
                            client.credit_score >= 600 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {client.credit_score}
                          </span>
                          <span className="text-xs text-gray-500">
                            {client.credit_score >= 700 ? 'Good' :
                             client.credit_score >= 600 ? 'Fair' : 'Poor'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not assessed</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${getStatusColor(client.status)}`}>
                        {capitalizeFirst(client.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`client-actions-${client.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewClient(client.id)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { viewClient(client.id); setEditMode(true); }}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {client.status === 'pending' && (
                            <DropdownMenuItem onClick={() => activateClient(client.id)}>
                              <UserCheck className="w-4 h-4 mr-2" /> Activate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/loans?action=new&client_id=${client.id}&client_name=${encodeURIComponent(client.first_name + ' ' + client.last_name)}`)}>
                            <FileText className="w-4 h-4 mr-2" /> New Loan
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * 20 + 1} to {Math.min((page + 1) * 20, total)} of {total} clients
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * 20 >= total}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* New Client Modal */}
      <NewClientModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={() => {
          setShowNewModal(false);
          loadClients();
        }}
      />

      {/* Client Detail Modal */}
      <ClientDetailModal
        open={showDetailModal}
        onClose={() => { setShowDetailModal(false); setEditMode(false); }}
        client={selectedClient}
        initialEditMode={editMode}
        onClientUpdated={() => {
          loadClients();
          if (selectedClient?.id) {
            clientsApi.get(selectedClient.id).then(res => setSelectedClient(res.data));
          }
        }}
      />
    </div>
  );
};

export const NewClientModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    id_number: '', id_type: 'national_id', date_of_birth: '',
    gender: '', address: '', city: '', province: '', postal_code: '',
    occupation: '', employer: '', employer_address: '', employer_contact_number: '', monthly_income: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const scanInputRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      api.get('/system-settings/public').then(res => {
        setOcrEnabled(res.data?.ocr_scanning_enabled || false);
      }).catch(() => {});
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = { ...formData };
      if (data.monthly_income) data.monthly_income = parseFloat(data.monthly_income);
      const res = await clientsApi.create(data);
      // Show screening result inline if present
      const screening = res?.data?.screening;
      if (screening?.status === 'match') {
        toast.error(`SANCTIONS MATCH — Client created but BLOCKED. ${screening.hit_count} hit(s). Compliance review required.`, { duration: 10000 });
      } else if (screening?.status === 'review') {
        toast.warning(`Possible PEP/sanctions match — ${screening.hit_count} hit(s) flagged for review. Open the Compliance tab on the new client.`, { duration: 8000 });
      } else if (screening?.status === 'clear') {
        toast.success('Client created. Sanctions screening: CLEAR.');
      } else {
        toast.success('Client created.');
      }
      onSuccess();
      setFormData({
        first_name: '', last_name: '', email: '', phone: '',
        id_number: '', id_type: 'national_id', date_of_birth: '',
        gender: '', address: '', city: '', province: '', postal_code: '',
        occupation: '', employer: '', employer_address: '', employer_contact_number: '', monthly_income: ''
      });
      setScanResult(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // KYC autofill from credit bureau (TransUnion / Experian / XDS) — triggered manually
  // by the "Lookup" button next to the ID Number input. Manual entry remains supported.
  const [kycLoading, setKycLoading] = useState(false);
  const [kycSettings, setKycSettings] = useState({ auto_trigger_on_blur: false, auto_trigger_min_length: 13 });

  useEffect(() => {
    api.get('/system-settings').then(r => {
      setKycSettings({
        auto_trigger_on_blur: !!r.data?.['kyc.auto_trigger_on_blur'],
        auto_trigger_min_length: r.data?.['kyc.auto_trigger_min_length'] || 13,
      });
    }).catch(() => {});
  }, []);

  const handleKycAutofill = async () => {
    const idn = (formData.id_number || '').trim();
    if (idn.length < 6) {
      toast.error('Enter the ID number first');
      return;
    }
    setKycLoading(true);
    try {
      const { data } = await api.get(`/integrations/kyc/autofill/${encodeURIComponent(idn)}`);
      if (!data.found) {
        if (data.error === 'no_credit_bureau_service') {
          toast.error('No credit bureau configured. Set one up in Admin → External Services.');
        } else {
          toast.warning(`Bureau lookup returned no profile (${data.error || 'not found'}). You can continue manually.`);
        }
        return;
      }
      const f = data.fields;
      setFormData(prev => ({
        ...prev,
        first_name: f.first_name || prev.first_name,
        last_name: f.last_name || prev.last_name,
        date_of_birth: f.date_of_birth || prev.date_of_birth,
        gender: f.gender ? String(f.gender).toLowerCase() : prev.gender,
        address: f.address || prev.address,
        phone: f.phone || prev.phone,
        email: f.email || prev.email,
        employer: f.employer || prev.employer,
      }));
      const filledCount = Object.values(f).filter(v => v).length;
      toast.success(`Prefilled ${filledCount} field(s) from ${data.source}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KYC lookup failed');
    }
    setKycLoading(false);
  };

  const handleScanDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const formPayload = new FormData();
      formPayload.append('file', file);
      const res = await api.post('/ocr/scan', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data;
      setScanResult(data);
      if (data.extracted_fields) {
        const f = data.extracted_fields;
        setFormData(prev => ({
          ...prev,
          first_name: f.first_name || prev.first_name,
          last_name: f.last_name || prev.last_name,
          id_number: f.id_number || prev.id_number,
          date_of_birth: f.date_of_birth || prev.date_of_birth,
          gender: f.gender ? f.gender.toLowerCase() : prev.gender,
          address: f.address || prev.address,
          phone: f.phone || prev.phone,
          email: f.email || prev.email,
          occupation: f.occupation || prev.occupation,
          employer: f.employer || prev.employer,
          employer_address: f.employer_address || prev.employer_address,
          employer_contact_number: f.employer_contact_number || prev.employer_contact_number,
          monthly_income: f.monthly_income || prev.monthly_income,
        }));
        toast.success(`Extracted ${Object.values(f).filter(v => v).length} field(s) from document`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Document scan failed';
      toast.error(detail);
    } finally {
      setScanning(false);
      if (scanInputRef.current) scanInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="new-client-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Client Registration</DialogTitle>
        </DialogHeader>
        <p id="new-client-desc" className="sr-only">Capture a new client's KYC profile. ID-Number Lookup is optional and pulls profile data from the configured KYC / credit bureau service.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* OCR Scan Section — only visible when enabled */}
          {ocrEnabled && (
            <div className="border border-[#C5A059]/40 bg-[#FFFDF5] p-4 rounded" data-testid="ocr-scan-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ScanLine className="w-5 h-5 text-[#C5A059]" />
                  <span className="text-sm font-semibold text-[#1f1f1f]">Scan Document (Optional)</span>
                </div>
                {scanResult && scanResult.confidence > 0 && (
                  <span className="text-xs px-2 py-0.5 bg-[#C5A059]/20 text-[#8B7339] rounded-full">
                    {Math.round(scanResult.confidence * 100)}% confidence
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-3">Upload a photo of an ID document or payslip to auto-fill fields. All fields can be manually corrected afterwards.</p>
              <div className="flex items-center gap-3">
                <input
                  ref={scanInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleScanDocument}
                  className="hidden"
                  data-testid="ocr-scan-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => scanInputRef.current?.click()}
                  disabled={scanning}
                  className="border-[#C5A059] text-[#C5A059] hover:bg-[#FFFDF5]"
                  data-testid="ocr-scan-button"
                >
                  {scanning ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
                      Scanning...
                    </span>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" />Scan ID / Payslip</>
                  )}
                </Button>
                {scanResult && scanResult.warnings?.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertCircle className="w-3 h-3" />
                    <span>{scanResult.warnings[0]}</span>
                  </div>
                )}
              </div>
              {scanResult && scanResult.document_description && (
                <p className="text-xs text-gray-500 mt-2">Detected: <span className="font-medium">{scanResult.document_description}</span></p>
              )}
            </div>
          )}

          <Tabs defaultValue="personal">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="personal">Personal Info</TabsTrigger>
              <TabsTrigger value="contact">Contact</TabsTrigger>
              <TabsTrigger value="employment">Employment</TabsTrigger>
            </TabsList>

            <TabsContent value="personal" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    required
                    data-testid="client-first-name"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    required
                    data-testid="client-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">ID Number *</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.id_number}
                      onChange={(e) => handleChange('id_number', e.target.value)}
                      onBlur={() => {
                        if (kycSettings.auto_trigger_on_blur && (formData.id_number || '').length >= kycSettings.auto_trigger_min_length) {
                          handleKycAutofill();
                        }
                      }}
                      required
                      data-testid="client-id-number"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={handleKycAutofill}
                            disabled={kycLoading}
                            className="shrink-0 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                            data-testid="kyc-autofill-btn"
                            title="Look up profile from KYC service / credit bureau">
                      {kycLoading ? '...' : 'Lookup'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {kycSettings.auto_trigger_on_blur
                      ? `Auto-lookup is ON (triggers when ${kycSettings.auto_trigger_min_length}+ digits entered). Toggle off in Admin → System → Settings.`
                      : 'Optional: click Lookup to autofill from KYC/credit bureau. Manual entry remains supported.'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">ID Type</Label>
                  <Select value={formData.id_type} onValueChange={(v) => handleChange('id_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="national_id">National ID</SelectItem>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="drivers_license">Driver's License</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Date of Birth</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start text-left font-normal h-10" data-testid="client-dob-trigger">
                        <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                        {formData.date_of_birth ? new Date(formData.date_of_birth).toLocaleDateString('en-ZA') : <span className="text-gray-400">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        fromYear={1920}
                        toYear={new Date().getFullYear()}
                        selected={formData.date_of_birth ? new Date(formData.date_of_birth) : undefined}
                        onSelect={(d) => handleChange('date_of_birth', d ? d.toISOString().slice(0, 10) : '')}
                        disabled={(d) => d > new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contact" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+27..."
                    required
                    data-testid="client-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Province</Label>
                  <Select value={formData.province} onValueChange={(v) => handleChange('province', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gauteng">Gauteng</SelectItem>
                      <SelectItem value="Western Cape">Western Cape</SelectItem>
                      <SelectItem value="KwaZulu-Natal">KwaZulu-Natal</SelectItem>
                      <SelectItem value="Eastern Cape">Eastern Cape</SelectItem>
                      <SelectItem value="Free State">Free State</SelectItem>
                      <SelectItem value="Limpopo">Limpopo</SelectItem>
                      <SelectItem value="Mpumalanga">Mpumalanga</SelectItem>
                      <SelectItem value="North West">North West</SelectItem>
                      <SelectItem value="Northern Cape">Northern Cape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Postal Code</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => handleChange('postal_code', e.target.value)}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="employment" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Occupation</Label>
                  <Input
                    value={formData.occupation}
                    onChange={(e) => handleChange('occupation', e.target.value)}
                    data-testid="new-client-occupation"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Employer</Label>
                  <Input
                    value={formData.employer}
                    onChange={(e) => handleChange('employer', e.target.value)}
                    data-testid="new-client-employer"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Employer Address</Label>
                <Input
                  value={formData.employer_address}
                  onChange={(e) => handleChange('employer_address', e.target.value)}
                  placeholder="Street, City, Province, Postal Code"
                  data-testid="new-client-employer-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Employer Contact Number</Label>
                  <Input
                    value={formData.employer_contact_number}
                    onChange={(e) => handleChange('employer_contact_number', e.target.value)}
                    placeholder="e.g., +27 11 555 1234"
                    data-testid="new-client-employer-contact"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Monthly Income (ZAR)</Label>
                  <Input
                    type="number"
                    value={formData.monthly_income}
                    onChange={(e) => handleChange('monthly_income', e.target.value)}
                    placeholder="0.00"
                    data-testid="new-client-monthly-income"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-new-client">
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ClientDetailModal = ({ open, onClose, client, onClientUpdated, initialEditMode = false }) => {
  const [uploadFile, setUploadFile] = useState(null);
  const [docType, setDocType] = useState('auto');
  const [uploading, setUploading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [ocrResult, setOcrResult] = useState(null);
  const [applyingOcr, setApplyingOcr] = useState(false);
  const [creditEval, setCreditEval] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);
  const [verifyingDocId, setVerifyingDocId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleDownloadLoanStatement = async (loanId, loanNumber) => {
    try {
      const res = await api.get(`/loans/${loanId}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `LoanStatement_${loanNumber}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Loan statement downloaded');
    } catch (e) {
      toast.error('Failed to download loan statement');
    }
  };

  const handleDownloadCombinedStatement = async () => {
    try {
      const res = await api.get(`/clients/${client.id}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CombinedStatement_${client.client_number}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Combined statement downloaded');
    } catch (e) {
      toast.error('Failed to download combined statement');
    }
  };

  useEffect(() => {
    if (client) {
      setDocs(client.documents || []);
      setCreditEval(null);
      setIsEditing(initialEditMode);
      setEditData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
        id_number: client.id_number || '',
        date_of_birth: client.date_of_birth || '',
        gender: client.gender || '',
        address: client.address || '',
        city: client.city || '',
        province: client.province || '',
        postal_code: client.postal_code || '',
        occupation: client.occupation || '',
        employer: client.employer || '',
        employer_address: client.employer_address || '',
        employer_contact_number: client.employer_contact_number || '',
        monthly_income: client.monthly_income || '',
      });
    }
  }, [client, initialEditMode]);

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      const data = { ...editData };
      if (data.monthly_income) data.monthly_income = parseFloat(data.monthly_income);
      await clientsApi.update(client.id, data);
      toast.success('Client updated successfully');
      setIsEditing(false);
      if (onClientUpdated) onClientUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update client');
    }
    setEditSaving(false);
  };

  if (!client) return null;

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setOcrResult(null);
    try {
      const res = await clientsApi.uploadDocument(client.id, uploadFile, docType);
      setDocs(prev => [...prev, res.data.document]);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (res.data.ocr_analysis && res.data.ocr_analysis.confidence > 0) {
        setOcrResult(res.data.ocr_analysis);
      }
    } catch (e) {
      console.error('Upload failed:', e);
    }
    setUploading(false);
  };

  const handleApplyOcr = async () => {
    if (!ocrResult?.extracted_fields) return;
    setApplyingOcr(true);
    try {
      await clientsApi.applyOcr(client.id, ocrResult.extracted_fields);
      setOcrResult(null);
      if (onClientUpdated) onClientUpdated();
    } catch (e) {
      console.error('Apply OCR failed:', e);
    }
    setApplyingOcr(false);
  };

  const handleDownload = async (doc) => {
    try {
      const res = await clientsApi.downloadDocument(client.id, doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await clientsApi.deleteDocument(client.id, docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const handleVerify = async (docId, status) => {
    setVerifyingDocId(docId);
    try {
      await clientsApi.verifyDocument(client.id, docId, status);
      setDocs(prev => prev.map(d =>
        d.id === docId ? { ...d, verification_status: status } : d
      ));
      if (onClientUpdated) onClientUpdated();
    } catch (e) {
      console.error('Verification update failed:', e);
    }
    setVerifyingDocId(null);
  };

  const verificationBadge = (status) => {
    const config = {
      verified: { icon: CheckCircle2, text: 'Verified', bg: 'bg-emerald-50', border: 'border-emerald-200', color: 'text-emerald-700' },
      needs_review: { icon: AlertTriangle, text: 'Needs Review', bg: 'bg-amber-50', border: 'border-amber-200', color: 'text-amber-700' },
      rejected: { icon: XCircle, text: 'Rejected', bg: 'bg-red-50', border: 'border-red-200', color: 'text-red-700' },
      pending: { icon: Clock, text: 'Pending', bg: 'bg-gray-50', border: 'border-gray-200', color: 'text-gray-500' },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.border} ${c.color} border`}>
        <Icon className="w-3 h-3" />{c.text}
      </span>
    );
  };

  const evaluateCredit = async () => {
    setEvalLoading(true);
    try {
      const { dashboardApi } = await import('../lib/api');
      const res = await dashboardApi.creditScoreConfig.evaluate(client.id);
      setCreditEval(res.data);
    } catch (e) {
      console.error('Credit evaluation failed:', e);
    }
    setEvalLoading(false);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const tierColorMap = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: 'bg-amber-500' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', bar: 'bg-orange-500' },
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500' },
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="client-detail-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-3">
            <div className="w-12 h-12 bg-[#1f1f1f] text-white flex items-center justify-center font-medium text-lg">
              {client.first_name?.[0]}{client.last_name?.[0]}
            </div>
            <div className="flex-1">
              <p>{client.first_name} {client.last_name}</p>
              <p className="text-sm font-normal text-gray-500">{client.client_number}</p>
            </div>
            <Button size="sm" variant="outline" className="text-xs" onClick={handleDownloadCombinedStatement} data-testid="combined-statement-btn">
              <FileText className="w-3.5 h-3.5 mr-1" />Combined Statement
            </Button>
          </DialogTitle>
        </DialogHeader>
        <p id="client-detail-desc" className="sr-only">Client details and documents</p>

        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">Information</TabsTrigger>
            <TabsTrigger value="credit" data-testid="credit-tab">Credit Profile</TabsTrigger>
            <TabsTrigger value="accounts">Accounts ({client.accounts?.length || 0})</TabsTrigger>
            <TabsTrigger value="loans">Loans ({client.loans?.length || 0})</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="compliance-tab">Compliance</TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">Documents ({docs.length})</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="contracts-tab">Contracts</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-6 pt-4">
            <div className="flex justify-end">
              {!isEditing ? (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="edit-client-btn">
                  <Edit className="w-4 h-4 mr-2" />Edit Client
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                  <Button size="sm" className="bg-[#1f1f1f] hover:bg-[#333333]" onClick={handleSaveEdit} disabled={editSaving} data-testid="save-client-btn">
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>

            {!isEditing ? (
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Personal Details</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">ID:</span>
                      <span className="font-medium">{client.id_number}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Phone:</span>
                      <span className="font-medium">{client.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Email:</span>
                      <span className="font-medium">{client.email || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Address:</span>
                      <span className="font-medium">
                        {client.address ? `${client.address}, ${client.city}, ${client.province}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Employment & Income</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-500">Occupation:</span>
                      <span className="font-medium">{client.occupation || '-'}</span>
                    </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Employer:</span>
                    <span className="font-medium ml-2">{client.employer || '-'}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Employer Address:</span>
                    <span className="font-medium ml-2">{client.employer_address || '-'}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Employer Contact:</span>
                    <span className="font-medium ml-2">{client.employer_contact_number || '-'}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-500">Monthly Income:</span>
                    <span className="font-medium ml-2">{formatCurrency(client.monthly_income)}</span>
                  </div>
                </div>
              </div>
            </div>
            ) : (
              /* Edit Mode Form */
              <div className="space-y-4" data-testid="edit-client-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">First Name</Label>
                    <Input value={editData.first_name} onChange={(e) => setEditData(p => ({...p, first_name: e.target.value}))} data-testid="edit-first-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Last Name</Label>
                    <Input value={editData.last_name} onChange={(e) => setEditData(p => ({...p, last_name: e.target.value}))} data-testid="edit-last-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Email</Label>
                    <Input value={editData.email} onChange={(e) => setEditData(p => ({...p, email: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Phone</Label>
                    <Input value={editData.phone} onChange={(e) => setEditData(p => ({...p, phone: e.target.value}))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">ID Number</Label>
                    <Input value={editData.id_number} onChange={(e) => setEditData(p => ({...p, id_number: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Date of Birth</Label>
                    <Input type="date" value={editData.date_of_birth} onChange={(e) => setEditData(p => ({...p, date_of_birth: e.target.value}))} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Address</Label>
                  <Input value={editData.address} onChange={(e) => setEditData(p => ({...p, address: e.target.value}))} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">City</Label>
                    <Input value={editData.city} onChange={(e) => setEditData(p => ({...p, city: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Province</Label>
                    <Input value={editData.province} onChange={(e) => setEditData(p => ({...p, province: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Postal Code</Label>
                    <Input value={editData.postal_code} onChange={(e) => setEditData(p => ({...p, postal_code: e.target.value}))} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Occupation</Label>
                    <Input value={editData.occupation} onChange={(e) => setEditData(p => ({...p, occupation: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Employer</Label>
                    <Input value={editData.employer} onChange={(e) => setEditData(p => ({...p, employer: e.target.value}))} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Monthly Income</Label>
                    <Input type="number" value={editData.monthly_income} onChange={(e) => setEditData(p => ({...p, monthly_income: e.target.value}))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Employer Address</Label>
                    <Input value={editData.employer_address} onChange={(e) => setEditData(p => ({...p, employer_address: e.target.value}))} placeholder="Street, City, Province, Postal Code" data-testid="edit-client-employer-address" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-gray-500">Employer Contact Number</Label>
                    <Input value={editData.employer_contact_number} onChange={(e) => setEditData(p => ({...p, employer_contact_number: e.target.value}))} placeholder="e.g., +27 11 555 1234" data-testid="edit-client-employer-contact" />
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Credit Profile Tab */}
          <TabsContent value="credit" className="space-y-4 pt-4" data-testid="credit-profile-panel">
            {/* Credit Score Display */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-5 border ${client.credit_tier ? `${tierColorMap[client.credit_tier.color]?.bg || 'bg-gray-50'} ${tierColorMap[client.credit_tier.color]?.border || 'border-gray-200'}` : 'bg-gray-50 border-gray-200'}`} data-testid="credit-score-card">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Credit Score</h4>
                {client.credit_score ? (
                  <div className="flex items-center gap-4">
                    <div className={`text-4xl font-bold ${tierColorMap[client.credit_tier?.color]?.text || 'text-gray-700'}`}>
                      {client.credit_score}
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${tierColorMap[client.credit_tier?.color]?.text || 'text-gray-700'}`}>
                        {client.credit_tier?.tier || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500">Updated: {formatDate(client.credit_score_date)}</p>
                      {/* Score Bar */}
                      <div className="w-40 h-2 bg-gray-200 mt-2 overflow-hidden">
                        <div
                          className={`h-full ${tierColorMap[client.credit_tier?.color]?.bar || 'bg-gray-500'}`}
                          style={{ width: `${Math.min((client.credit_score / 850) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-400 w-40 mt-0.5">
                        <span>300</span><span>850</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">Not assessed</p>
                )}
              </div>

              {/* Interest Rate Card */}
              <div className="p-5 bg-blue-50 border border-blue-200" data-testid="interest-rate-card">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Effective Loan Rate (Floating)</h4>
                {client.effective_loan_rate ? (
                  <div>
                    <div className="text-4xl font-bold text-blue-700">{client.effective_loan_rate}%</div>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">SARB Repo Rate</span>
                        <span className="font-mono font-medium">{client.current_repo_rate}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Credit Spread</span>
                        <span className="font-mono font-medium">+{client.credit_tier?.spread || '-'}%</span>
                      </div>
                      <div className="border-t border-blue-200 pt-1 flex justify-between text-xs font-semibold">
                        <span>Effective Rate</span>
                        <span className="font-mono">{client.effective_loan_rate}%</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-blue-600 mt-2">Rate = Repo ({client.current_repo_rate}%) + Spread ({client.credit_tier?.spread}%) for floating-rate products</p>
                  </div>
                ) : (
                  <p className="text-gray-400">No rate calculable without credit score</p>
                )}
              </div>
            </div>

            {/* DTI and Obligations */}
            <div className="p-5 bg-white border border-gray-200" data-testid="dti-card">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Debt-to-Income Analysis</h4>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div>
                  <p className="text-xs text-gray-500">Monthly Income</p>
                  <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(client.monthly_income)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Monthly Obligations</p>
                  <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(client.total_monthly_obligations)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">DTI Ratio</p>
                  <p className={`text-lg font-bold ${client.dti_ratio > 40 ? 'text-red-600' : client.dti_ratio > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {client.dti_ratio}%
                  </p>
                </div>
              </div>
              {/* DTI Bar */}
              <div className="w-full bg-gray-100 h-3 overflow-hidden">
                <div
                  className={`h-full transition-all ${client.dti_ratio > 40 ? 'bg-red-500' : client.dti_ratio > 30 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(client.dti_ratio, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                <span>0%</span>
                <span className="text-amber-500">30%</span>
                <span className="text-red-500">40%</span>
                <span>100%</span>
              </div>
              {client.credit_tier && (
                <p className="text-xs text-gray-500 mt-2">
                  Max DTI for {client.credit_tier.tier} tier: <span className="font-semibold">{client.credit_tier.max_dti}%</span>
                  {client.dti_ratio > client.credit_tier.max_dti && (
                    <span className="text-red-600 ml-2 font-semibold">Exceeds limit</span>
                  )}
                </p>
              )}
            </div>

            {/* Pre-Check Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={evaluateCredit}
                disabled={evalLoading}
                className="bg-[#C5A059] hover:bg-[#B89249] text-white"
                data-testid="evaluate-credit-btn"
              >
                {evalLoading ? 'Evaluating...' : 'Run Lending Pre-Check'}
              </Button>
              <span className="text-xs text-gray-400">Evaluates against head office credit score cut-offs</span>
            </div>

            {/* Evaluation Result */}
            {creditEval && (
              <div className={`p-4 border ${creditEval.eligible ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="credit-eval-result">
                <div className="flex items-center gap-2 mb-2">
                  {creditEval.eligible ? (
                    <span className="text-emerald-700 font-semibold text-sm">Pre-Check: Eligible</span>
                  ) : (
                    <span className="text-red-700 font-semibold text-sm">Pre-Check: Not Eligible</span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    creditEval.decision === 'auto_approve' ? 'bg-emerald-200 text-emerald-800' :
                    creditEval.decision === 'manual_review' ? 'bg-amber-200 text-amber-800' :
                    'bg-red-200 text-red-800'
                  }`}>
                    {creditEval.decision?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div><span className="text-gray-500 block">Tier</span><span className="font-medium">{creditEval.tier?.name || '-'}</span></div>
                  <div><span className="text-gray-500 block">Effective Rate</span><span className="font-medium">{creditEval.effective_rate}%</span></div>
                  <div><span className="text-gray-500 block">DTI Ratio</span><span className="font-medium">{creditEval.dti_ratio}%</span></div>
                  <div><span className="text-gray-500 block">DTI Limit</span><span className="font-medium">{creditEval.dti_limit}%</span></div>
                </div>
                {!creditEval.auto_decision_enabled && (
                  <p className="text-[10px] text-gray-500 mt-2">Automated lending decisions are currently disabled. All loans require manual approval.</p>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="accounts" className="pt-4">
            {client.accounts?.length > 0 ? (
              <div className="space-y-3">
                {client.accounts.map((acc) => (
                  <div key={acc.id} className="p-4 border border-gray-200 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{acc.account_number}</p>
                      <p className="text-sm text-gray-500">{capitalizeFirst(acc.account_type)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{formatCurrency(acc.balance)}</p>
                      <span className={`text-xs px-2 py-0.5 ${getStatusColor(acc.status)}`}>
                        {capitalizeFirst(acc.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No accounts found</p>
            )}
          </TabsContent>

          <TabsContent value="loans" className="pt-4">
            {client.loans?.length > 0 ? (
              <div className="space-y-3">
                {client.loans.map((loan) => (
                  <div key={loan.id} className="p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">{loan.loan_number}</p>
                        <p className="text-sm text-gray-500">
                          {formatCurrency(loan.principal || loan.amount)} &middot; {loan.term_months} months
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{formatCurrency(loan.outstanding_balance)}</p>
                        <span className={`text-xs px-2 py-0.5 ${getStatusColor(loan.status)}`}>
                          {capitalizeFirst(loan.status)}
                        </span>
                      </div>
                    </div>
                    {/* Credit Scores */}
                    <div className="flex gap-4 text-xs text-gray-500 mb-2">
                      {loan.account_credit_score != null && (
                        <span>Account Score: <span className="font-bold text-[#C5A059]" data-testid={`loan-acct-score-${loan.id}`}>{loan.account_credit_score}</span></span>
                      )}
                      {loan.group_credit_score != null && (
                        <span>Group Score: <span className="font-bold text-[#C5A059]" data-testid={`loan-group-score-${loan.id}`}>{loan.group_credit_score}</span></span>
                      )}
                      {loan.credit_score_at_application != null && (
                        <span>Client Score (at application): <span className="font-medium">{loan.credit_score_at_application}</span></span>
                      )}
                    </div>
                    {/* Rate Details */}
                    <div className="flex gap-4 text-xs text-gray-500 border-t border-gray-100 pt-2 mt-2">
                      <span>Rate: <span className="font-medium text-[#1f1f1f]">{loan.interest_rate}%</span></span>
                      <span>Mode: <span className="font-medium text-[#1f1f1f] capitalize">{loan.interest_rate_mode || 'fixed'}</span></span>
                      {loan.repo_rate_at_origination && (
                        <span>Repo at origination: <span className="font-medium">{loan.repo_rate_at_origination}%</span></span>
                      )}
                      {loan.credit_spread != null && (
                        <span>Spread: <span className="font-medium">+{loan.credit_spread}%</span></span>
                      )}
                      <span className="ml-auto">
                        <Button size="sm" variant="outline" className="text-xs h-6 px-2" onClick={() => handleDownloadLoanStatement(loan.id, loan.loan_number)} data-testid={`loan-statement-${loan.id}`}>
                          <FileText className="w-3 h-3 mr-1" />Statement
                        </Button>
                      </span>
                    </div>
                    {/* Covenant Status */}
                    {loan.covenant_status && (
                      <div className="border-t border-gray-100 pt-2 mt-2" data-testid={`loan-covenant-${loan.id}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${loan.covenant_status.breached ? 'bg-red-500' : 'bg-emerald-500'}`} />
                          <span className="text-xs font-semibold text-gray-600">Covenants:</span>
                          {loan.covenant_status.covenants?.map((c, i) => (
                            <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${c.compliant ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                              {c.label.split(' ').slice(0, 2).join(' ')} {c.current_value?.toFixed(2)} {c.operator}{c.threshold}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No loans found</p>
            )}
          </TabsContent>

          <TabsContent value="compliance" className="pt-4" data-testid="compliance-panel">
            <div className="space-y-4">
              <KYCTrustPanel client={client} onReload={onClientUpdated} />
              <ClientComplianceTab client={client} onReload={onClientUpdated} />
            </div>
          </TabsContent>

          <TabsContent value="documents" className="pt-4" data-testid="documents-panel">
            {/* Document Verification Summary */}
            {docs.length > 0 && (
              <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 border border-gray-200 rounded" data-testid="doc-verification-summary">
                <ShieldCheck className="w-5 h-5 text-[#1f1f1f]" />
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gray-600 font-medium">Documents:</span>
                  <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" />{docs.filter(d => d.verification_status === 'verified').length} Verified</span>
                  <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" />{docs.filter(d => d.verification_status === 'needs_review').length} Review</span>
                  <span className="inline-flex items-center gap-1 text-gray-500"><Clock className="w-3 h-3" />{docs.filter(d => !d.verification_status || d.verification_status === 'pending').length} Pending</span>
                  <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="w-3 h-3" />{docs.filter(d => d.verification_status === 'rejected').length} Rejected</span>
                </div>
              </div>
            )}
            {/* Upload Section */}
            <div className="border border-dashed border-gray-300 p-4 mb-4">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1">Document Type</label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm"
                    data-testid="doc-type-select"
                  >
                    <option value="auto">Auto-detect (OCR)</option>
                    <option value="identification">National ID / Passport</option>
                    <option value="proof_of_address">Proof of Address</option>
                    <option value="proof_of_income">Proof of Income</option>
                    <option value="bank_statement">Bank Statement</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1">File (images for OCR)</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full text-sm file:mr-2 file:py-1 file:px-3 file:border-0 file:text-xs file:bg-[#1f1f1f] file:text-white file:cursor-pointer"
                    data-testid="doc-file-input"
                  />
                </div>
                <Button
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  className="bg-[#1f1f1f] hover:bg-[#333333] h-9"
                  data-testid="upload-doc-button"
                >
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Analyzing...
                    </span>
                  ) : 'Upload & Analyze'}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-2">Upload JPG/PNG images of documents for automatic OCR classification and field extraction</p>
            </div>

            {/* OCR Results */}
            {ocrResult && ocrResult.confidence > 0 && (
              <div className="border border-[#C5A059] bg-[#FFFDF5] p-4 mb-4" data-testid="ocr-results">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-[#C5A059]" />
                    <h4 className="text-sm font-semibold text-[#1f1f1f]">OCR Analysis Result</h4>
                    <span className="text-xs px-2 py-0.5 bg-[#C5A059]/20 text-[#8B7339] rounded-full">
                      {Math.round(ocrResult.confidence * 100)}% confidence
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleApplyOcr}
                      disabled={applyingOcr}
                      className="bg-[#C5A059] hover:bg-[#B89249] text-white text-xs h-7"
                      data-testid="apply-ocr-button"
                    >
                      {applyingOcr ? 'Applying...' : 'Apply to Client Record'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOcrResult(null)}
                      className="text-xs h-7"
                      data-testid="dismiss-ocr-button"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  Detected: <span className="font-medium">{ocrResult.document_type?.replace(/_/g, ' ')}</span>
                  {ocrResult.document_description && ` — ${ocrResult.document_description}`}
                </p>
                {ocrResult.extracted_fields && Object.entries(ocrResult.extracted_fields).filter(([, v]) => v).length > 0 && (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2">
                    {Object.entries(ocrResult.extracted_fields).filter(([, v]) => v).map(([key, val]) => (
                      <div key={key} className="flex items-center text-xs py-1">
                        <span className="text-gray-500 w-28 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium text-[#1f1f1f]">{val}</span>
                      </div>
                    ))}
                  </div>
                )}
                {ocrResult.warnings?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-600">
                    {ocrResult.warnings.map((w, i) => <p key={i}>{w}</p>)}
                  </div>
                )}
              </div>
            )}

            {/* Documents List */}
            {docs.length > 0 ? (
              <div className="space-y-2">
                {docs.map((doc) => (
                  <div key={doc.id} className="p-3 border border-gray-200 hover:bg-gray-50" data-testid={`doc-row-${doc.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-[#C5A059]" />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-[#1f1f1f]">{doc.name}</p>
                            {verificationBadge(doc.verification_status)}
                          </div>
                          <p className="text-xs text-gray-500">
                            {doc.type?.replace(/_/g, ' ')} {doc.size ? `\u2022 ${formatSize(doc.size)}` : ''} \u2022 {formatDate(doc.uploaded_at)}
                            {doc.ocr_analysis?.confidence > 0 && (
                              <span className="ml-1 text-[#C5A059]">\u2022 OCR: {Math.round(doc.ocr_analysis.confidence * 100)}%</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1 items-center">
                        {/* Verification action buttons */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              disabled={verifyingDocId === doc.id}
                              data-testid={`verify-doc-${doc.id}`}
                            >
                              {verifyingDocId === doc.id ? 'Updating...' : 'Verify'}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleVerify(doc.id, 'verified')} className="text-emerald-700 text-xs" data-testid={`mark-verified-${doc.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-2" />Mark as Verified
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleVerify(doc.id, 'needs_review')} className="text-amber-700 text-xs" data-testid={`mark-review-${doc.id}`}>
                              <AlertTriangle className="w-3.5 h-3.5 mr-2" />Needs Review
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleVerify(doc.id, 'rejected')} className="text-red-700 text-xs" data-testid={`mark-rejected-${doc.id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-2" />Reject
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleVerify(doc.id, 'pending')} className="text-gray-500 text-xs" data-testid={`mark-pending-${doc.id}`}>
                              <Clock className="w-3.5 h-3.5 mr-2" />Reset to Pending
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {doc.ocr_analysis?.confidence > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[#C5A059] border-[#C5A059]/30 hover:bg-[#FFFDF5] text-xs h-7"
                            onClick={() => setOcrResult(doc.ocr_analysis)}
                            data-testid={`view-ocr-${doc.id}`}
                          >
                            View OCR
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                          Download
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                          Delete
                        </Button>
                      </div>
                    </div>
                    {doc.verification_note && (
                      <p className="text-xs text-gray-500 mt-1 ml-9">Note: {doc.verification_note}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No documents uploaded</p>
                <p className="text-xs text-gray-400 mt-1">Upload ID documents, proof of address, or income documents for automatic OCR analysis</p>
              </div>
            )}
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts" className="pt-4" data-testid="contracts-panel">
            <ContractsPanel clientId={client.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ====== Contracts Panel Component ======
const ContractsPanel = ({ clientId }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingFor, setSigningFor] = useState(null);
  const [signatureMode, setSignatureMode] = useState('digital'); // 'digital' or 'upload'
  const [uploadedSigFile, setUploadedSigFile] = useState(null);
  const canvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);

  const loadContracts = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/contracts`);
      setContracts(res.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadContracts(); }, [clientId]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1f1f1f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  };

  useEffect(() => { if (signingFor) setTimeout(initCanvas, 100); }, [signingFor]);

  const handleMouseDown = (e) => {
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleMouseMove = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleMouseUp = () => { isDrawingRef.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    let sigData;
    if (signatureMode === 'digital') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      sigData = canvas.toDataURL('image/png').split(',')[1];
    } else if (uploadedSigFile) {
      sigData = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.readAsDataURL(uploadedSigFile);
      });
    } else {
      toast.error('Please draw a signature or upload a document');
      return;
    }

    try {
      await api.post(`/clients/${clientId}/sign-contract`, {
        product_id: signingFor.product_id,
        account_id: signingFor.account_id,
        signature_type: signatureMode,
        signature_data: sigData,
      });
      toast.success('Contract signed');
      setSigningFor(null);
      loadContracts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign');
    }
  };

  const handleDownloadContract = async (productId, fileName) => {
    try {
      const res = await api.get(`/loan-products/${productId}/contract/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'contract.pdf';
      a.click();
    } catch (e) {
      toast.error('No contract available for download');
    }
  };

  const handlePrint = async (productId) => {
    try {
      const res = await api.get(`/loan-products/${productId}/contract/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const w = window.open(url);
      if (w) w.print();
    } catch (e) { toast.error('Cannot print contract'); }
  };

  // Get products that have contracts for "sign new" dropdown
  const [availableProducts, setAvailableProducts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/loan-products');
        setAvailableProducts(res.data.filter(p => p.contract_file_name));
      } catch(e) { /* ignore */ }
    })();
  }, []);

  if (loading) return <p className="text-center py-8 text-gray-400 text-sm">Loading contracts...</p>;

  return (
    <div className="space-y-4" data-testid="contracts-panel-content">
      {/* Available products to sign */}
      {availableProducts.length > 0 && (
        <div className="p-3 bg-gray-50 border border-gray-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Sign Product Contract</p>
          <div className="flex flex-wrap gap-2">
            {availableProducts.map(p => (
              <div key={p.id} className="flex items-center gap-2 p-2 bg-white border border-gray-200 text-xs">
                <FileText className="w-3.5 h-3.5 text-gray-400" />
                <span className="font-medium">{p.name}</span>
                <span className="text-gray-400">(v{p.contract_version})</span>
                <Button size="sm" variant="outline" className="h-5 text-[10px] px-2" onClick={() => handleDownloadContract(p.id, p.contract_file_name)}>View</Button>
                <Button size="sm" variant="outline" className="h-5 text-[10px] px-2" onClick={() => handlePrint(p.id)}>Print</Button>
                <Button size="sm" className="h-5 text-[10px] px-2 bg-[#1f1f1f] text-white" onClick={() => setSigningFor({ product_id: p.id, product_name: p.name })} data-testid={`sign-contract-${p.id}`}>Sign</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {signingFor && (
        <div className="p-4 border-2 border-[#C5A059] bg-[#FFFDF5]" data-testid="signature-area">
          <h4 className="text-sm font-bold text-[#1f1f1f] mb-2">Sign Contract: {signingFor.product_name}</h4>
          <div className="flex gap-4 mb-3">
            <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 border text-xs ${signatureMode === 'digital' ? 'border-[#C5A059] bg-[#C5A059]/10' : 'border-gray-200'}`}>
              <input type="radio" value="digital" checked={signatureMode === 'digital'} onChange={() => setSignatureMode('digital')} />
              Draw Signature
            </label>
            <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 border text-xs ${signatureMode === 'upload' ? 'border-[#C5A059] bg-[#C5A059]/10' : 'border-gray-200'}`}>
              <input type="radio" value="upload" checked={signatureMode === 'upload'} onChange={() => setSignatureMode('upload')} />
              Upload Signed Document
            </label>
          </div>

          {signatureMode === 'digital' ? (
            <div>
              <canvas ref={canvasRef} width={400} height={150} className="border border-gray-300 bg-white cursor-crosshair"
                onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
                data-testid="signature-canvas" />
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={clearCanvas} className="text-xs">Clear</Button>
                <Button size="sm" onClick={handleSign} className="text-xs bg-[#1f1f1f] text-white" data-testid="submit-signature">Submit Signature</Button>
                <Button size="sm" variant="outline" onClick={() => setSigningFor(null)} className="text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setUploadedSigFile(e.target.files?.[0])} className="text-xs mb-2" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSign} disabled={!uploadedSigFile} className="text-xs bg-[#1f1f1f] text-white" data-testid="submit-upload-signature">Upload Signed Document</Button>
                <Button size="sm" variant="outline" onClick={() => setSigningFor(null)} className="text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Signed Contracts List */}
      {contracts.length > 0 ? (
        <div className="space-y-2">
          {contracts.map(c => (
            <div key={c.id} className="p-3 border border-gray-200 flex items-center justify-between" data-testid={`signed-contract-${c.id}`}>
              <div>
                <p className="text-sm font-medium text-[#1f1f1f]">{c.product_name}</p>
                <p className="text-xs text-gray-500">
                  Signed: {c.signed_at?.slice(0, 10)} | Version {c.contract_version} | Type: {c.signature_type}
                </p>
              </div>
              <div className="flex gap-2">
                {(c.contract_file_url || c.contract_has_data) && (
                  <>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleDownloadContract(c.product_id, c.contract_file_name)} data-testid={`download-contract-${c.id}`}>Download</Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handlePrint(c.product_id)}>Print</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No contracts signed yet</p>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
